import { Link } from "react-router-dom";
import { SiteHeader, SiteFooter } from "@/pages/LandingPage";
import { Trophy, ClipboardList, Zap, BarChart3, Megaphone, ScrollText, Share2, Shield, Timer, ArrowLeft } from "lucide-react";

/** Public guide page — commercial content, no auth. Uses Sudefootball blue palette. */
export default function PublicGuidePage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:text-blue-900 mb-6">
          <ArrowLeft size={14} /> Volver al inicio
        </Link>

        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">Guía rápida</div>
        <h1 data-testid="guide-title" className="mt-3 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter uppercase leading-[0.95] text-slate-900">¿Qué es Sudefootball?</h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Sudefootball es una <span className="text-slate-900 font-bold">plataforma SaaS diseñada específicamente para clubes de fútbol base y amateur</span>.
          Convierte la gestión deportiva en algo digital, ágil y trazable — desde el trabajo diario del entrenador hasta la visión global del coordinador.
        </p>

        <Section title="Míralo en vídeo">
          <p className="text-sm text-slate-600 mb-5">Dos minutos para ver cómo funciona — y el vídeo dedicado a la Pizarra de partido en vivo.</p>
          <div className="flex flex-wrap gap-6">
            <div className="w-full sm:w-[260px]">
              <video
                data-testid="guide-video-overview"
                src="/descargas/sudefootball-3min.mp4"
                poster="/descargas/poster-3min.jpg"
                controls preload="metadata" playsInline
                className="w-full rounded-sm border-2 border-slate-200 bg-black aspect-[9/16]"
              />
              <div className="mt-2 text-xs font-bold text-slate-700 uppercase tracking-wider">Sudefootball en 2 minutos</div>
            </div>
            <div className="w-full sm:w-[260px]">
              <video
                data-testid="guide-video-pizarra"
                src="/descargas/pizarra-en-vivo.mp4"
                poster="/descargas/poster-pizarra.jpg"
                controls preload="metadata" playsInline
                className="w-full rounded-sm border-2 border-slate-200 bg-black aspect-[9/16]"
              />
              <div className="mt-2 text-xs font-bold text-slate-700 uppercase tracking-wider">La Pizarra: partido en vivo</div>
            </div>
          </div>
        </Section>

        <Section title="Lo que tu club puede hacer">
          <Feature icon={Trophy} title="Gestión estructurada">Categorías, equipos y plantillas jerárquicas. Roles Admin/Coordinador/Entrenador con permisos precisos.</Feature>
          <Feature icon={ClipboardList} title="Análisis del jugador">Plantillas de puntuación totalmente editables por categoría (U6 a U16 y Portero). Bloques e indicadores personalizables al gusto del club — o del equipo específico.</Feature>
          <Feature icon={Zap} title="Registro rápido de jornadas">Alta de partidos y entrenamientos con métricas por jugador. Auto-convocatoria con modo rotación para dar minutos a todos. Guarda borradores durante el partido y cierra la jornada como el acta de un árbitro.</Feature>
          <Feature icon={Timer} title="Asistencia mensual">Marca quién asistió al entrenamiento con vista de calendario. Justificación objetiva de las convocatorias a fin de temporada.</Feature>
          <Feature icon={BarChart3} title="Estadísticas globales">Panel filtrable por categoría, equipo, jugador y rango de fechas. Exporta a Excel con un click y descarga el PDF individual del jugador.</Feature>
          <Feature icon={Megaphone} title="Portal de comunicación">Convocatorias y anuncios enviados por email a los padres, con botones RSVP para que confirmen asistencia con un click.</Feature>
          <Feature icon={Share2} title="Panel público del jugador">El entrenador genera un enlace único con foto y estadísticas del niño. La familia lo comparte por WhatsApp con abuelos, amigos y familiares.</Feature>
          <Feature icon={ScrollText} title="Trazabilidad y control">Historial de cambios completo (quién editó qué y cuándo). Suspensión automática por impago con avisos previos. PWA instalable en el móvil.</Feature>
          <Feature icon={Shield} title="Datos aislados">Multi-tenant: cada club tiene su espacio privado. Nadie ve lo que no le corresponde.</Feature>
        </Section>

        <Section title="Roles del club">
          <RoleRow role="Administrador" desc="Gestión completa del club: staff, categorías, equipos, plantillas, comunicación e historial." />
          <RoleRow role="Coordinador" desc="Vista global del club, plantillas, estadísticas, historial y comunicación con familias." />
          <RoleRow role="Entrenador" desc="Sus equipos, jornadas, asistencia, estadísticas y comunicación directa con las familias de sus jugadores." />
        </Section>

        <Section title="Cómo empezar en 6 pasos">
          <Step n="01" title="Alta del club">Sudefootball da de alta tu club y crea el usuario del Administrador.</Step>
          <Step n="02" title="Categorías">Crea las categorías (U8, U10, U12…) y elige la plantilla base.</Step>
          <Step n="03" title="Equipos">Da de alta los equipos dentro de cada categoría.</Step>
          <Step n="04" title="Equipo técnico">Invita a coordinadores y entrenadores desde el menú Equipo Técnico.</Step>
          <Step n="05" title="Jugadores">Cada entrenador añade a sus jugadores con contactos de padres.</Step>
          <Step n="06" title="Empieza a registrar">Jornadas, asistencia, evaluaciones. Al final: PDF por jugador + Excel de temporada.</Step>
        </Section>

        <Section title="Ventajas frente a Excel o WhatsApp">
          <ul className="space-y-3 text-sm leading-relaxed">
            <Advantage>Datos siempre disponibles desde móvil, tablet o PC.</Advantage>
            <Advantage>La familia siempre ve la información desde el enlace público — no hay que reenviar Excel.</Advantage>
            <Advantage>Quién editó qué queda registrado. Trazabilidad total.</Advantage>
            <Advantage>Bienvenida, convocatoria, RSVP y aviso de impago se envían solos por email.</Advantage>
            <Advantage>Coeficientes objetivos basados en observación semanal, no en impresiones.</Advantage>
          </ul>
        </Section>

        <div className="mt-16 border-2 border-blue-700 bg-blue-50 p-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">¿Quieres esta plataforma en tu club?</div>
          <h3 className="mt-2 text-3xl font-black uppercase tracking-tight text-slate-900">Contacta con Sudefootball</h3>
          <p className="text-sm text-slate-600 mt-3">Escríbenos y te acompañamos en la puesta en marcha.</p>
          <a href="mailto:sudefootball@sudeporte.com" className="inline-flex items-center gap-2 h-12 px-6 mt-5 bg-blue-700 text-white hover:bg-blue-800 font-heading uppercase tracking-wider text-sm" data-testid="guide-contact-link">
            sudefootball@sudeporte.com
          </a>
        </div>

        <div className="mt-12 text-center text-xs text-slate-500 uppercase tracking-widest">
          Sudefootball · by Sudeporte
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-14">
      <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700">{title}</div>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function Feature({ icon: Icon, title, children }) {
  return (
    <div className="border border-slate-200 bg-white p-5 flex items-start gap-4 hover:border-blue-700 hover:shadow-md transition-all">
      <div className="w-10 h-10 border-2 border-blue-700 bg-blue-50 flex items-center justify-center shrink-0 text-blue-700">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="font-black uppercase tracking-tight text-slate-900">{title}</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function RoleRow({ role, desc }) {
  return (
    <div className="border border-slate-200 p-5">
      <div className="font-black uppercase tracking-tight text-blue-700">{role}</div>
      <p className="text-sm text-slate-600 mt-1">{desc}</p>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex items-start gap-4 border-l-4 border-blue-700 pl-4 py-2 bg-blue-50/40">
      <div className="font-black text-3xl text-blue-300 leading-none">{n}</div>
      <div>
        <div className="font-black uppercase tracking-tight text-slate-900">{title}</div>
        <p className="text-sm text-slate-600 mt-1">{children}</p>
      </div>
    </div>
  );
}

function Advantage({ children }) {
  return (
    <li className="flex items-start gap-3 text-slate-700">
      <span className="text-blue-700 mt-1 font-bold">◆</span>
      <span>{children}</span>
    </li>
  );
}
